import { Film } from "@definitions/film";
import {
    FETCH_FILMS_REQUEST,
    FETCH_FILMS_SUCCESS,
    FETCH_FILMS_FAILURE,
    CLEAR_FILMS_CACHE,
    INVALIDATE_FILMS_TAG,
} from "./film-action-types";
import { FilmActions } from "./film-actions";

export interface FilmCacheEntry {
    films: Film[];
    totalCount: number;
    page: number;
    timestamp: number;
    isLoading: boolean;
    error: string | null;
    isInvalidated: boolean;
}

export interface FilmState {
    cache: {
        [cacheKey: string]: FilmCacheEntry;
    };
}

const initialState: FilmState = {
    cache: {},
};

/**
 * Film reducer with caching support
 */
const filmReducer = (state = initialState, action: FilmActions): FilmState => {
    switch (action.type) {
        case FETCH_FILMS_REQUEST:
            return {
                ...state,
                cache: {
                    ...state.cache,
                    [action.payload.cacheKey]: {
                        ...(state.cache[action.payload.cacheKey] || {}),
                        isLoading: true,
                        error: null,
                    } as FilmCacheEntry,
                },
            };

        case FETCH_FILMS_SUCCESS:
            return {
                ...state,
                cache: {
                    ...state.cache,
                    [action.payload.cacheKey]: {
                        films: action.payload.films,
                        totalCount: action.payload.totalCount,
                        page: action.payload.page,
                        timestamp: action.payload.timestamp,
                        isLoading: false,
                        error: null,
                        isInvalidated: false,
                    },
                },
            };

        case FETCH_FILMS_FAILURE:
            return {
                ...state,
                cache: {
                    ...state.cache,
                    [action.payload.cacheKey]: {
                        ...(state.cache[action.payload.cacheKey] || {}),
                        isLoading: false,
                        error: action.payload.error,
                    } as FilmCacheEntry,
                },
            };

        case CLEAR_FILMS_CACHE:
            return {
                ...state,
                cache: {},
            };

        case INVALIDATE_FILMS_TAG:
            // Mark all caches containing this tag as invalidated
            const updatedCache = { ...state.cache };
            Object.keys(updatedCache).forEach((cacheKey) => {
                try {
                    const query = JSON.parse(cacheKey);
                    if (query.tags && query.tags.includes(action.payload.tag)) {
                        updatedCache[cacheKey] = {
                            ...updatedCache[cacheKey],
                            isInvalidated: true,
                        };
                    }
                } catch (e) {
                    // Invalid cache key format, skip
                }
            });

            return {
                ...state,
                cache: updatedCache,
            };

        default:
            return state;
    }
};

export default filmReducer;
